// ./src/utils/api/EventFinanceService.ts
//
// Event finance service — the ticketed-event P&L / ROI read (#29). OpResult-native,
// event-scoped. A single `summary` query returning the event's rolled-up P&L across
// the payment ledger + budget + partners, computed server-side (mirrors the budget
// summary; there is no CRUD). See em-scaffold-ui.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";

const EVENT_FINANCIAL_SUMMARY_QUERY = gql`
  query EventFinancialSummary($filter: EventFinancialSummaryFilter) {
    eventFinancialSummary(filter: $filter) {
      code
      data {
        eventId
        currencyCode
        mixedCurrency
        ticketRevenue
        ticketGross
        ticketRefunded
        ticketOrderCount
        donationRevenue
        membershipRevenue
        otherSalesRevenue
        partnerRevenueActual
        partnerRevenueAgreed
        serviceFeeTotal
        processorFeeTotal
        feesEstimated
        budgetPaid
        budgetExpected
        partnerExpenseActual
        partnerExpenseAgreed
        revenueActual
        revenueProjected
        costActual
        costProjected
        netActual
        netProjected
        roiActual
        roiProjected
        marginActual
        breakEvenRevenue
        breakEvenReached
        breakEvenPct
        eventCapacity
        ticketTypeBreakdown {
          ticketTypeId name isAddOn addonCategoryId addonCategoryName listPrice quantityTotal unitsSold grossSold
        }
        addonBreakdown {
          ticketTypeId name isAddOn addonCategoryId addonCategoryName listPrice quantityTotal unitsSold grossSold
        }
        budgetCategoryBreakdown {
          categoryId categoryName expected paid
        }
        staffPayByRole {
          roleId roleName expected actual total
        }
      }
      errors { name errors warnings }
    }
  }
`;

// =============================================================================
// Types (match the eventsmagic-api EventFinancialSummary schema)
// =============================================================================

export interface FinancialSummary {
  eventId?: string | null;
  currencyCode?: string | null;
  /** True when host-revenue rows span >1 currency — totals then mix units (warning). */
  mixedCurrency?: boolean | null;

  // Revenue (net of refunds)
  ticketRevenue?: number | null;
  ticketGross?: number | null;
  ticketRefunded?: number | null;
  ticketOrderCount?: number | null;
  donationRevenue?: number | null;
  membershipRevenue?: number | null;
  otherSalesRevenue?: number | null;
  partnerRevenueActual?: number | null;
  partnerRevenueAgreed?: number | null;
  serviceFeeTotal?: number | null;
  /** The card fees — what Stripe really took once read back (#201), the checkout estimate until then (see feesEstimated). */
  processorFeeTotal?: number | null;
  /** True → at least one sale's card fee is still the checkout estimate; the UI labels the line "est.". */
  feesEstimated?: boolean | null;

  // Costs
  budgetPaid?: number | null;
  budgetExpected?: number | null;
  partnerExpenseActual?: number | null;
  partnerExpenseAgreed?: number | null;

  // Bottom line
  revenueActual?: number | null;
  revenueProjected?: number | null;
  costActual?: number | null;
  costProjected?: number | null;
  netActual?: number | null;
  netProjected?: number | null;
  /** ROI as a ratio (0.35 = +35%); null when there is no cost. */
  roiActual?: number | null;
  roiProjected?: number | null;
  /** Profit margin as a ratio; null when there is no revenue. */
  marginActual?: number | null;
  /** Revenue needed to cover all projected costs (= costProjected). */
  breakEvenRevenue?: number | null;
  breakEvenReached?: boolean | null;
  /** Progress toward break-even as a ratio (revenueActual / costProjected). */
  breakEvenPct?: number | null;

  /** Event headcount capacity (for the forecast's ideal-sales projection). */
  eventCapacity?: number | null;
  /** Sales per admission ticket type (units + gross), best-sellers first. */
  ticketTypeBreakdown?: FinancialLineItem[] | null;
  /** Sales per add-on (units + gross), each with its add-on category. */
  addonBreakdown?: FinancialLineItem[] | null;
  /** Budget expected + paid grouped by category. */
  budgetCategoryBreakdown?: FinancialCategoryLine[] | null;
  /** #A4.7 — the Staff pay budget line split by volunteer role (already inside the budget costs). */
  staffPayByRole?: FinancialStaffPayRole[] | null;
}

/** #A4.7 — staff pay for one role: expected (shifts ahead + open places), actual (finished shifts), total. */
export interface FinancialStaffPayRole {
  /** Null = shifts with no role. */
  roleId?: string | null;
  roleName?: string | null;
  expected?: number | null;
  actual?: number | null;
  total?: number | null;
}

/** One sellable line (ticket type or add-on): sold units + gross, plus list price + cap for the forecast. */
export interface FinancialLineItem {
  ticketTypeId?: string | null;
  name?: string | null;
  isAddOn?: boolean | null;
  addonCategoryId?: string | null;
  addonCategoryName?: string | null;
  listPrice?: number | null;
  quantityTotal?: number | null;
  unitsSold?: number | null;
  grossSold?: number | null;
}

/** Budget expected + paid for one category (null category → Uncategorized on the client). */
export interface FinancialCategoryLine {
  categoryId?: string | null;
  categoryName?: string | null;
  expected?: number | null;
  paid?: number | null;
}

// =============================================================================
// Service
// =============================================================================

export class EventFinanceService {
  /** The event's P&L / ROI rollup across the ledger + budget + partners. */
  static async summary(eventId: string): Promise<OpResult> {
    return ApiService.executeQuery(EVENT_FINANCIAL_SUMMARY_QUERY, { filter: { eventId: [eventId] } });
  }
}
